// src/components/pdf/AdvancedStatsPage2.jsx
import React from 'react';
import { Page, Text, View } from '@react-pdf/renderer';
import styles from './styles';
import { SimplePieChart, VerticalBarChart, CommentBox } from './charts/AdvancedCharts';
import { SHOT_COLORS } from '../../utils/advancedStatsCalculator';

/**
 * Page 2 des statistiques avancées
 * GAUCHE: Coups victoire + Aces/Services par zone + commentaires
 * DROITE: Coups défaite + Retours par zone + commentaires
 */
const AdvancedStatsPage2 = ({ match, playerName, playerCode, advancedStats, comments }) => {
  const { zoneStats } = advancedStats;
  
  // Récupérer le nom du joueur courant (avec fallback sécurisé)
  // Les noms peuvent être soit dans match.joueur1.nom soit directement dans match.joueur1
  const currentPlayerName = playerName || 
    (playerCode === 'J1' 
      ? (match?.joueur1?.nom || match?.joueur1 || 'Joueur 1') 
      : (match?.joueur2?.nom || match?.joueur2 || 'Joueur 2'));
  
  // Récupérer le nom de l'adversaire (avec fallback sécurisé)
  const opponentName = playerCode === 'J1' 
    ? (match?.joueur2?.nom || match?.joueur2 || 'Adversaire') 
    : (match?.joueur1?.nom || match?.joueur1 || 'Adversaire');
  
  // Calculer les avantages pour le joueur actuel
  const calculatePlayerAdvantages = () => {
    const comparison = advancedStats.comparison;
    const j1 = comparison.j1;
    const j2 = comparison.j2;
    
    const myPlayer = playerCode === 'J1' ? j1 : j2;
    const opponent = playerCode === 'J1' ? j2 : j1;
    
    const advantages = [];
    const disadvantages = [];
    
    // Points gagnants
    const pointsStats = [
      { label: 'Service', diff: myPlayer.pointsService - opponent.pointsService },
      { label: 'Retour', diff: myPlayer.pointsRetour - opponent.pointsRetour },
      { label: 'Coup Droit', diff: myPlayer.pointsCoupDroit - opponent.pointsCoupDroit },
      { label: 'Revers', diff: myPlayer.pointsRevers - opponent.pointsRevers },
      { label: 'Volée', diff: myPlayer.pointsVolee - opponent.pointsVolee }
    ];
    
    // Fautes
    const fautesStats = [
      { label: 'Service', diff: myPlayer.fautesService - opponent.fautesService },
      { label: 'Retour', diff: myPlayer.fautesRetour - opponent.fautesRetour },
      { label: 'Coup Droit', diff: myPlayer.fautesCoupDroit - opponent.fautesCoupDroit },
      { label: 'Revers', diff: myPlayer.fautesRevers - opponent.fautesRevers },
      { label: 'Volée', diff: myPlayer.fautesVolee - opponent.fautesVolee }
    ];
    
    // Traiter points gagnants
    pointsStats.forEach(stat => {
      if (stat.diff === 0) return;
      if (stat.diff > 0) {
        advantages.push({ name: stat.label, value: stat.diff, color: SHOT_COLORS[stat.label] });
      } else {
        disadvantages.push({ name: stat.label, value: Math.abs(stat.diff), color: SHOT_COLORS[stat.label] });
      }
    });
    
    // Traiter fautes (moins de fautes = avantage)
    fautesStats.forEach(stat => {
      if (stat.diff === 0) return;
      if (stat.diff < 0) {
        advantages.push({ name: `Fautes ${stat.label}`, value: Math.abs(stat.diff), color: SHOT_COLORS[stat.label] });
      } else {
        disadvantages.push({ name: `Fautes ${stat.label}`, value: stat.diff, color: SHOT_COLORS[stat.label] });
      }
    });
    
    return { advantages, disadvantages };
  };
  
  const playerAdvantages = calculatePlayerAdvantages();
  
  // Préparer les données par zones pour le joueur
  const playerIndex = playerCode === 'J1' ? 'j1' : 'j2';
  
  // Données pour graphe Aces/Services
  const serviceData = zoneStats.zoneLabels.map((label, idx) => ({
    label: label,
    points: zoneStats.serviceStats[playerIndex].aces[idx],
    fautes: zoneStats.serviceStats[playerIndex].doubleFautes[idx]
  })); // SANS FILTER - garder tous les items
  
  // Données pour graphe Retours
  const returnData = zoneStats.zoneLabels.map((label, idx) => ({
    label: label,
    points: zoneStats.retourStats[playerIndex].points[idx],
    fautes: zoneStats.retourStats[playerIndex].fautes[idx]
  })); // SANS FILTER - garder tous les items
  
  return (
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionTitle}>Statistiques Avancées - Analyse par Zones</Text>
      
      {/* Espacement après le titre */}
      <View style={{ height: 10 }} />
      
      {/* Layout 2 colonnes */}
      <View style={{ flexDirection: 'row', gap: 20, marginTop: 10 }}>
        {/* COLONNE GAUCHE */}
        <View style={{ flex: 1 }}>
          {/* Donut coups expliquant la victoire */}
          <SimplePieChart
            data={playerAdvantages.advantages}
            title="Coups expliquant la victoire (ou limitant la défaite)"
            subtitle={`(Coups à l'avantage de ${currentPlayerName})`}
            width={250}
            height={200}
          />
        </View>
        
        {/* COLONNE DROITE */}
        <View style={{ flex: 1 }}>
          {/* Donut coups expliquant la défaite */}
          <SimplePieChart
            data={playerAdvantages.disadvantages}
            title="Coups expliquant la défaite (ou limitant la victoire)"
            subtitle={`(Coups à l'avantage de ${opponentName})`}
            width={250}
            height={200}
          />
        </View>
      </View>
      
      {/* Commentaire commun aux 2 donuts victoire/défaite - pleine largeur, APRÈS les donuts */}
      {comments?.bloc3 && (
        <View style={{ marginTop: -10 }}>
          <CommentBox comment={comments.bloc3} />
        </View>
      )}
      
      {/* Espacement entre les blocs de graphes */}
      <View style={{ height: 15 }} />
      
      {/* Layout 2 colonnes pour les graphes en barres */}
      <View style={{ flexDirection: 'row', gap: 20 }}>
        {/* COLONNE GAUCHE */}
        <View style={{ flex: 1 }}>
          {/* Graphe Aces / Services par zone */}
          <VerticalBarChart
            data={serviceData}
            title="Aces / Services gagnants par zone"
            color={SHOT_COLORS['Service']}
          />
        </View>
        
        {/* COLONNE DROITE */}
        <View style={{ flex: 1 }}>
          {/* Graphe Retours par zone */}
          <VerticalBarChart
            data={returnData}
            title="Retours gagnants par zone"
            color={SHOT_COLORS['Retour']}
          />
        </View>
      </View>
      
      {/* Commentaire commun aux 2 graphes Aces/Retours - pleine largeur, APRÈS les graphes barres */}
      {comments?.bloc4 && (
        <View style={{ marginTop: -10 }}>
          <CommentBox comment={comments.bloc4} />
        </View>
      )}
    </Page>
  );
};

export default AdvancedStatsPage2;